import { MAX_ALTERNATE_CTAS, applyOwnerLayout } from '@promo-popup-contract';
import type {
  AlternateCta,
  CtaAction,
  CtaButton,
  PopupConditionId,
  PopupContent,
} from '@promo-popup-contract';
import { isSafeExternalUrl } from '@/helpers/openExternal';
import { isConnectTier, isLiveSubscriptionStatus } from '@/utils/subscriptions';
import { ROUTES } from '@/types/routes';

/**
 * The subset of the Redux profile these conditions read. Everything here
 * already arrives from getProfile, which is why evaluation happens on the
 * frontend: deciding server-side would cost a second Stripe call on a hot path
 * to choose which marketing copy to show.
 */
export interface PopupViewer {
  subscriptionInfo?: {
    tier?: string | null;
    normalizedStatus?: string | null;
  } | null;
  premiumDashboardUrl?: string;
  isFreeListing?: boolean;
}

/**
 * TCX-917 — a `Record<PopupConditionId, ...>`, not a `switch`, so TypeScript
 * REQUIRES an entry for every condition id the contract declares. A `switch`
 * that falls through to `default: return false` compiles fine when a branch
 * is forgotten — the new condition simply never matches, and the admin
 * configures a fallback button that silently never appears. A missing key
 * here is instead a compile error, at the moment a condition is added to
 * `POPUP_CONDITIONS_BY_AUDIENCE`, not a silently dead button discovered later.
 */
// Exported for `promoPopupConditions.spec.ts` only, so the "every condition
// has a branch" test can assert against the actual predicate table instead of
// re-deriving it — a `switch`-shaped assertion here would not fail the way a
// missing `case` needs it to.
export const CONDITION_PREDICATES: Record<PopupConditionId, (user?: PopupViewer) => boolean> = {
  /**
   * TCX-917 — every condition FAILS CLOSED.
   *
   * An absent `subscriptionInfo` (Stripe unreachable, profile still loading,
   * the subscription fully ended) is NOT evidence of entitlement, so
   * `not-full-member` is TRUE with no signal and the refusal alternate shows
   * rather than the premium button. This is the stance TCX-914 took and the
   * reason is unchanged: there is no backend backstop here — whichever
   * button renders is the one the user gets.
   *
   * `tier === 'full'` is asserted POSITIVELY rather than `tier !== 'connect'`,
   * so any future tier that is neither lands on the safe side by default.
   */
  'not-full-member': (user) => user?.subscriptionInfo?.tier !== 'full',

  'on-connect-tier': (user) => isConnectTier(user),

  'is-free-listing': (user) => user?.isFreeListing === true,

  // isSafeExternalUrl, not `!!url`: openExternal refuses non-http(s), so a
  // javascript: value must read as "no dashboard", not as "available".
  'no-premium-dashboard': (user) => !isSafeExternalUrl(user?.premiumDashboardUrl),

  'subscription-inactive': (user) =>
    !isLiveSubscriptionStatus(user?.subscriptionInfo?.normalizedStatus),
};

export const evaluateCondition = (
  condition: PopupConditionId,
  user?: PopupViewer,
): boolean => {
  const predicate = CONDITION_PREDICATES[condition];
  // Reachable when content is saved by a server newer than this bundle: the
  // id is not a key of the record above (its type only covers ids THIS
  // bundle's contract knows about), so `predicate` is undefined. An unknown
  // condition must not match — an alternate nobody can explain should stay
  // hidden.
  if (!predicate) return false;
  return predicate(user);
};

/**
 * TCX-917 increment (preset redirect actions) — where a `CtaAction` actually
 * leads for THIS viewer. `url` and `support` are unchanged from before this
 * increment: a static admin-typed url always resolves (once it has passed
 * `isHttpUrl` at save time; `isSafeExternalUrl` here is the same http(s)-only
 * check under the name this file already uses for the dashboard link) and
 * `support` always opens the modal. The two internal-route presets always
 * resolve — they point at a fixed page, not at anything on the viewer's
 * profile. `premium-dashboard` is the ONE case that can fail: it reads
 * `premiumDashboardUrl` off the viewer's own profile, which may be unset or
 * (defensively) unsafe.
 *
 * `undefined` means "this action cannot be carried out for this viewer" —
 * the caller (`resolveButton` below, for a rule; the click handlers in
 * PromoPopup, for whichever button actually renders) is what turns that into
 * "skip this candidate" or "do nothing on click", never a button that opens
 * nothing.
 */
export type ResolvedCtaDestination =
  | { kind: 'external'; url: string }
  | { kind: 'support' }
  | { kind: 'internal'; path: string };

export const resolveCtaDestination = (
  action: CtaAction,
  user?: PopupViewer,
): ResolvedCtaDestination | undefined => {
  switch (action.type) {
    case 'url':
      return isSafeExternalUrl(action.url) ? { kind: 'external', url: action.url } : undefined;
    case 'support':
      return { kind: 'support' };
    case 'premium-dashboard': {
      const dashboardUrl = user?.premiumDashboardUrl;
      return typeof dashboardUrl === 'string' && isSafeExternalUrl(dashboardUrl)
        ? { kind: 'external', url: dashboardUrl }
        : undefined;
    }
    case 'edit-profile':
      return { kind: 'internal', path: ROUTES.MY_PROFILE_GENERAL };
    case 'subscription':
      return { kind: 'internal', path: ROUTES.MY_PROFILE_SUBSCRIPTION };
    default:
      // Reachable when content is saved by a server newer than this bundle —
      // same "unknown id cannot match" stance `evaluateCondition` takes.
      return undefined;
  }
};

/**
 * Array order IS priority: the first matching alternate wins, and `fallback`
 * is what shows when nothing matches (or there is nothing to check).
 *
 * TCX-917 increment — the ONE function behind BOTH `resolveCta` (the primary)
 * and `resolveSecondaryCta` (the second button): each button's rules are
 * evaluated by the exact same walk, just against that button's own
 * `fallback`/`alternates` pair, so the two buttons can never drift into two
 * near-identical copies of "first match wins".
 *
 * TCX-917 increment (preset redirect actions) — a candidate must not just
 * MATCH its condition, its action must also RESOLVE for this viewer. A rule
 * set to "Redirect to premium dashboard" on a franchisor with no dashboard
 * saved matches `no-premium-dashboard` perfectly well but has nothing to
 * open — rendering it anyway is exactly the "button that does nothing on
 * click" bug the previous franchisor pop-up shipped. Skipping it here falls
 * through to the next matching candidate, and ultimately to `fallback` (the
 * owner button), which is never itself filtered this way — it is the
 * guaranteed backstop every chain ends at, same as before this increment.
 */
const resolveButton = (
  fallback: CtaButton,
  alternates: AlternateCta[] | undefined,
  user?: PopupViewer,
): CtaButton => {
  const match = alternates
    // Bounded on read as well as on write: content saved by a newer server, or
    // edited by hand in Mongo, must not let an unbounded list decide what a
    // user sees.
    ?.slice(0, MAX_ALTERNATE_CTAS)
    .find(
      (alternate) =>
        evaluateCondition(alternate.condition, user) &&
        resolveCtaDestination(alternate.action, user) !== undefined,
    );
  // TCX-917 increment (rule style inheritance) — a matched rule NEVER
  // supplies its own variant/fullWidth/align/font*; `applyOwnerLayout` always
  // overrides those six fields with `fallback`'s (the owner button's) own
  // values, even when the matched rule still carries a stale stored value for
  // one of them (a record saved before this increment).
  return match ? applyOwnerLayout(fallback, match) : fallback;
};

/**
 * The primary button, resolved against its own rules.
 */
export const resolveCta = (
  content: PopupContent,
  user?: PopupViewer,
): CtaButton => resolveButton(content.cta, content.alternateCtas, user);

/**
 * TCX-917 increment — the secondary button, resolved against its OWN rules,
 * completely independently of the primary's. `undefined` when the pop-up has
 * no secondary configured at all — the caller (PromoPopup, PopupCard) treats
 * an absent secondary as "render only one button", not as a resolution
 * failure.
 */
export const resolveSecondaryCta = (
  content: PopupContent,
  user?: PopupViewer,
): CtaButton | undefined =>
  content.secondaryCta
    ? resolveButton(content.secondaryCta.button, content.secondaryCta.alternateCtas, user)
    : undefined;
